import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { Bouton, CaseACocher, Champ, Message } from '@journal/ui';
import { CoquilleAuth, LienAuth } from '../components/CoquilleAuth.tsx';
import { messageErreur, useSession } from '../lib/session.tsx';
import { VERSION_SOUS_TRAITANCE } from './Legal.tsx';
import {
  AIDE_MOT_DE_PASSE, LONGUEUR_MINIMALE_MOT_DE_PASSE,
} from '../lib/mot-de-passe.ts';

/**
 * Création d'un espace.
 *
 * ── Le sous-domaine n'est plus demandé ──────────────────────────────────────
 * Il l'était (« Adresse de l'espace »), d'abord réclamé puis proposé à partir
 * du nom de l'école. Mais on travaille sur l'adresse canonique `app.` : ce
 * sous-domaine ne sert qu'en option. Le serveur le déduit désormais du nom de
 * l'école et le rend unique (`slugPourEcole`, côté API).
 */


/**
 * Les deux profils, avec ce qui les distingue vraiment.
 *
 * Le libellé seul ne suffit pas : « École » pourrait s'entendre comme « je
 * travaille dans une école », ce qui est vrai des deux. La phrase dit ce qui
 * change — plusieurs comptes, ou un seul.
 */
export const PROFILS = [
  {
    valeur: 'ENSEIGNANT' as const,
    titre: 'Un enseignant seul',
    detail: 'Votre classe, votre espace. Un compte.',
  },
  {
    valeur: 'ECOLE' as const,
    titre: 'Une école',
    detail: 'Vous gérez les comptes de vos collègues depuis votre espace.',
  },
];

export function Inscription() {
  const { api } = useSession();
  const naviguer = useNavigate();

  const [nomEcole, setNomEcole] = useState('');
  const [profil, setProfil] = useState<'ENSEIGNANT' | 'ECOLE'>('ENSEIGNANT');
  const [contratAccepte, setContratAccepte] = useState(false);
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  async function soumettre(evenement: FormEvent) {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      await api.requete('/auth/register', {
        methode: 'POST',
        /*
         * Pas d'identifiant d'espace : le serveur le déduit du nom de l'école.
         * Le formulaire le demandait (« Adresse de l'espace »), alors qu'on
         * travaille sur l'adresse canonique `app.` et que ce sous-domaine ne
         * sert qu'en option — un choix technique imposé à l'inscription, que
         * le remplissage automatique du navigateur remplissait parfois avec
         * l'adresse électronique.
         */
        corps: {
          email, password: motDePasse, schoolName: nomEcole,
          firstName: prenom.trim(), lastName: nom.trim(),
          profile: profil,
          // La VERSION affichée, et non un simple « oui ». Le serveur refuse
          // celle qu'il ne connaît pas : modifier le texte sans changer la
          // constante ferait signer un contrat sous le nom d'un autre.
          sousTraitance: VERSION_SOUS_TRAITANCE,
        },
        sansRafraichissement: true,
      });
      // On ne connecte pas automatiquement : l'adresse n'est pas encore
      // vérifiée, et l'écran suivant doit dire quoi faire du courriel reçu.
      naviguer(`/inscription/confirmation?email=${encodeURIComponent(email)}`);
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  }

  return (
    <CoquilleAuth
      titre="Créer un espace"
      // La durée de l'essai ne figurait nulle part : on créait un espace sans
      // savoir combien de temps on pourrait l'utiliser avant de décider.
      sousTitre="Trente jours d’essai, sans engagement. Aucune carte bancaire n’est demandée."
      pied={
        <>
          <p>
            Vous avez déjà un compte ? <LienAuth vers="/">Se connecter</LienAuth>
          </p>
          <p>
            <LienAuth vers="/offres">Voir les offres</LienAuth>
          </p>
          <p>
            <LienAuth vers="/documentation">Lire la documentation</LienAuth>
            {' '}: chaque écran, en images.
          </p>
          {/*
            * ── Pour celui qui REVIENT ────────────────────────────────────────
            * Un espace supprimé au terme du délai de grâce est effacé pour de
            * bon — la loi l'impose, et rien n'en subsiste à restaurer. Sans
            * cette ligne, celui qui se réinscrit croit récupérer son travail,
            * trouve un espace vide, et pense l'application cassée. Le dire
            * AVANT lui évite de découvrir la mauvaise nouvelle après.
            *
            * Dit sans condition, faute de pouvoir détecter le cas : après la
            * purge, il ne reste précisément aucune trace à interroger.
            */}
          <p>
            Cet espace part vide. Si vous en aviez un qui a été supprimé, ses
            données ne peuvent pas être remises : passé le délai annoncé,
            l’effacement est définitif.{' '}
            <LienAuth vers="/contact">Nous écrire</LienAuth> en cas de doute.
          </p>
        </>
      }
    >
      <form onSubmit={soumettre} className="flex flex-col gap-4" noValidate>
        {/*
          * Le profil ouvre le formulaire, il ne le clôt pas.
          *
          * C'est la question qui décide de tout le reste — le vocabulaire de
          * l'écran, ce qui sera proposé pendant l'essai, l'offre vers laquelle
          * on ira. La poser après le mot de passe, en case à cocher, la ferait
          * passer pour une option ; c'est un aiguillage.
          *
          * Deux boutons plutôt qu'une liste déroulante : il n'y a que deux
          * réponses, et chacune mérite sa phrase d'explication.
          */}
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-[13px] font-medium">Vous êtes…</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {PROFILS.map((p) => (
              <button
                key={p.valeur}
                type="button"
                aria-pressed={profil === p.valeur}
                onClick={() => setProfil(p.valeur)}
                className={[
                  'rounded-xl border p-3 text-left transition-colors duration-150',
                  'motion-reduce:transition-none',
                  'focus-visible:outline-2 focus-visible:outline-offset-2',
                  'focus-visible:outline-[var(--color-focus)]',
                  profil === p.valeur
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary-soft)]'
                    : 'border-[var(--color-border)] hover:bg-[var(--color-surface-hover)]',
                ].join(' ')}
              >
                <span className="block text-[13px] font-semibold">{p.titre}</span>
                <span className="mt-0.5 block text-[12px] leading-snug text-[var(--color-fg-muted)]">
                  {p.detail}
                </span>
              </button>
            ))}
          </div>
        </fieldset>

        {/*
          * ── QUI ouvre l'espace ─────────────────────────────────────────────
          * Avant le nom de l'école, parce que c'est la personne qui s'inscrit,
          * pas l'établissement. Les deux se confondent chez un enseignant seul
          * dont l'espace porte son propre nom — raison de plus pour poser la
          * question séparément.
          *
          * Le nom sert ensuite partout où l'application désigne quelqu'un :
          * en bas de la barre latérale, et dans la liste de l'équipe.
          */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Champ
            libelle="Prénom"
            value={prenom}
            onChange={(e) => setPrenom(e.target.value)}
            autoComplete="given-name"
            maxLength={60}
            required
          />
          <Champ
            libelle="Nom"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            autoComplete="family-name"
            maxLength={60}
            required
          />
        </div>

        <Champ
          // Libellé fixe : « école » vaut pour les deux profils — un enseignant
          // seul exerce aussi dans une école — et faire changer le vocabulaire
          // sous les doigts au moment du choix donne l'impression que le
          // formulaire se réécrit.
          libelle="Nom de l'école"
          value={nomEcole}
          onChange={(e) => setNomEcole(e.target.value)}
          autoComplete="organization"
          required
        />
        <Champ
          libelle="Adresse électronique"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          required
        />
        <Champ
          libelle="Mot de passe"
          type="password"
          value={motDePasse}
          onChange={(e) => setMotDePasse(e.target.value)}
          // `new-password` évite que le gestionnaire propose un mot de
          // passe existant là où il faut en générer un.
          autoComplete="new-password"
          aide={AIDE_MOT_DE_PASSE}
          minLength={LONGUEUR_MINIMALE_MOT_DE_PASSE}
          required
        />

        {/*
          * ── Le contrat, à l'endroit où l'on s'engage ────────────────────────
          * L'article 28 du RGPD exige un contrat écrit entre l'école —
          * responsable de traitement — et l'éditeur, sous-traitant. Une école
          * le demandera avant de confier des noms d'élèves ; le lui faire
          * accepter ici, en le montrant, vaut mieux qu'une clause enfouie dans
          * des conditions que personne n'ouvre.
          *
          * La case est OBLIGATOIRE : sans elle, le contrat n'existe pas, et
          * l'espace se créerait avec des données de mineurs sans cadre
          * opposable.
          */}
        <CaseACocher
          libelle={
            <>
              J’accepte les{' '}
              <Link to="/cgv" className="underline">conditions générales</Link>
              {' '}et le{' '}
              <Link to="/confidentialite" className="underline">
                contrat de sous-traitance
              </Link>
              , qui encadre le traitement des données d’élèves.
            </>
          }
          checked={contratAccepte}
          onChange={(e) => setContratAccepte(e.target.checked)}
          required
        />

        {erreur && <Message ton="erreur">{erreur}</Message>}

        <Bouton
          type="submit"
          variante="primary"
          taille="lg"
          chargement={enCours}
          className="w-full"
        >
          Créer l'espace
        </Bouton>
      </form>
    </CoquilleAuth>
  );
}
